import axios from 'axios';
import {useCallback} from 'react';
import raiseFlashMessage from '@/utils/raiseFlashMessage';
import translate from '@/utils/translate';
import downloadStore, {
  DownloadStatus,
  downloadStoreActions,
} from '@/stores/downloadStore';

const DONE_DISPLAY_MS = 2000;

export type DownloadTarget = {
  id: string;
  url: string | null;
  fileName?: string;
};

export type UseDownloadReturn = {
  status: DownloadStatus | 'idle';
  progress: number;
  download: () => void;
};

function useDownload({id, url, fileName}: DownloadTarget): UseDownloadReturn {
  const entry = downloadStore((s) => s.downloads[id]);
  const status = entry?.status ?? 'idle';
  const progress = entry?.progress ?? 0;

  const download = useCallback(() => {
    if (entry?.status === 'downloading') {
      return;
    }

    if (!url) {
      raiseFlashMessage({
        status: 'error',
        message: translate('No file available to download'),
      });
      return;
    }

    downloadStoreActions.startDownload(id);

    axios
      .get<Blob>(url, {
        responseType: 'blob',
        onDownloadProgress: (event) => {
          if (event.total) {
            const pct = Math.round((event.loaded / event.total) * 100);
            downloadStoreActions.updateProgress(id, pct);
          }
        },
      })
      .then(({data}) => {
        downloadStoreActions.completeDownload(id);

        const objectUrl = URL.createObjectURL(data);
        const a = document.createElement('a');
        a.href = objectUrl;
        a.download = fileName ?? '';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(objectUrl);

        setTimeout(
          () => downloadStoreActions.clearDownload(id),
          DONE_DISPLAY_MS
        );
      })
      .catch(() => {
        downloadStoreActions.failDownload(id);
        raiseFlashMessage({
          status: 'error',
          message: translate('Download failed — please try again'),
        });
        setTimeout(
          () => downloadStoreActions.clearDownload(id),
          DONE_DISPLAY_MS
        );
      });
  }, [id, url, fileName, entry?.status]);

  return {status, progress, download};
}

export default useDownload;
